/**
 * Delivery Status Message
 *
 * Contextual message based on delivery health.
 * Calm, advisory tone - never alarming.
 *
 * PRINCIPLES:
 * - Reassuring language — the client paid for leads, we're delivering them
 * - Actionable when appropriate
 * - Never blame the system or create unnecessary concern
 * - Include concrete numbers when helpful
 */

import { CheckCircle, Clock, TrendingUp, Info, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DeliveryStatus } from "./DeliveryOverviewCard";

interface DeliveryStatusMessageProps {
  status: DeliveryStatus;
  daysRemaining?: number;
  remainingLeads?: number;
  className?: string;
}

interface StatusMessage {
  icon: typeof CheckCircle;
  title: string;
  message: string;
  suggestion?: string;
  iconClass: string;
  bgClass: string;
}

function getStatusMessage(
  status: DeliveryStatus,
  daysRemaining?: number,
  remainingLeads?: number
): StatusMessage {
  switch (status) {
    case 'ON_TRACK':
      return {
        icon: CheckCircle,
        title: "Delivery is on track",
        message: remainingLeads && daysRemaining
          ? `${remainingLeads} leads remaining with ${daysRemaining} days left in your cycle — right on schedule.`
          : "Your lead delivery is progressing as planned.",
        iconClass: "text-green-600",
        bgClass: "bg-green-50 border-green-100",
      };

    case 'SLIGHTLY_BEHIND':
      return {
        icon: Clock,
        title: "Delivery pace is adjusting",
        message: remainingLeads && daysRemaining
          ? `${remainingLeads} leads remaining with ${daysRemaining} days left. Delivery is slightly behind the original pace.`
          : "Your leads are being delivered, but the pace is a bit slower than planned.",
        suggestion: "This is normal — lead volume varies day to day. Your account manager is monitoring your delivery.",
        iconClass: "text-amber-600",
        bgClass: "bg-amber-50 border-amber-100",
      };

    case 'AT_RISK':
      return {
        icon: Info,
        title: "Delivery is behind schedule",
        message: remainingLeads && daysRemaining
          ? `${remainingLeads} leads remaining with ${daysRemaining} days left — the delivery pace needs to increase to complete on time.`
          : "The current delivery pace may not complete your plan on time.",
        suggestion: "Our team is aware and working to get you back on track. Any undelivered leads will carry over to your next cycle.",
        iconClass: "text-orange-600",
        bgClass: "bg-orange-50 border-orange-100",
      };

    case 'COMPLETE':
      return {
        icon: Sparkles,
        title: "Delivery complete!",
        message: "All leads in your plan have been delivered.",
        iconClass: "text-primary",
        bgClass: "bg-primary/5 border-primary/10",
      };

    case 'OVER_DELIVERED':
      return {
        icon: TrendingUp,
        title: "Bonus leads delivered",
        message: "You've received more leads than your plan includes. Keep up the great work!",
        iconClass: "text-primary",
        bgClass: "bg-primary/5 border-primary/10",
      };

    default:
      return {
        icon: CheckCircle,
        title: "Status update",
        message: "Your delivery is being processed.",
        iconClass: "text-muted-foreground",
        bgClass: "bg-muted border-border",
      };
  }
}

export function DeliveryStatusMessage({
  status,
  daysRemaining,
  remainingLeads,
  className,
}: DeliveryStatusMessageProps) {
  const statusMessage = getStatusMessage(status, daysRemaining, remainingLeads);
  const Icon = statusMessage.icon;

  return (
    <div className={cn(
      "rounded-xl border p-4",
      statusMessage.bgClass,
      className
    )}>
      <div className="flex items-start gap-3">
        <div className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center bg-white/80",
          statusMessage.iconClass
        )}>
          <Icon className="w-5 h-5" />
        </div>

        <div className="flex-1">
          <h4 className="font-semibold text-foreground">{statusMessage.title}</h4>
          <p className="text-sm text-muted-foreground mt-0.5">{statusMessage.message}</p>

          {statusMessage.suggestion && (
            <p className="text-sm text-foreground/80 mt-2 font-medium">
              {statusMessage.suggestion}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
